import {
  BookedSkatingPass,
  BookingData,
  Coupon,
  HockeyLessonData,
  ParticipantInfo,
  PartyData,
  SkaterInfo,
  SkatingLessonData,
  SkatingPassData,
  SquarePaymentInfo,
} from '@types';
import { useAppSelector } from '_hooks';
import { HelperMethods } from '_utils';
import React, { useEffect, useRef, useState } from 'react';
import {
  MdCheckCircle,
  MdCreditCard,
  MdError,
  MdReceipt,
} from 'react-icons/md';
import { AvailablePasses } from './AvailablePasses';
import GetCouponDetails from './GetCouponDetails';

const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID || '';
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID || '';
const SQUARE_SCRIPT_URL = process.env.REACT_APP_SQUARE_SCRIPT_URL || '';

interface PaymentProps {
  paymentType: 'skating' | 'hockey' | 'lessons' | 'passes' | 'parties';
  bookingData:
    | BookingData
    | HockeyLessonData
    | SkatingLessonData
    | SkatingPassData
    | PartyData;
  onPrevious: () => void;
  onStepComplete: (data: any) => void;
}

export const Payment: React.FC<PaymentProps> = ({
  paymentType,
  bookingData,
  onPrevious,
  onStepComplete,
}) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const [card, setCard] = useState<any>(null);
  const [status, setStatus] = useState('');
  const [isCardLoading, setIsCardLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const isInitializedRef = useRef(isInitialized);
  isInitializedRef.current = isInitialized;
  const calculateFinalTotalRef = useRef<() => number>(() => 0);
  const [hasError, setHasError] = useState(false);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [selectedAdultPasses, setSelectedAdultPasses] = useState<
    BookedSkatingPass[]
  >([]);
  const [selectedChildPasses, setSelectedChildPasses] = useState<
    BookedSkatingPass[]
  >([]);

  // Get authentication state from Redux store
  const { isAuthenticated, accountDetails: accountDetailsState } =
    useAppSelector((state: any) => state.auth);

  // Check if user has admin role
  const isAdmin = () => {
    if (!accountDetailsState?.roleKey) return false;
    return ['admin', 'super_admin'].includes(accountDetailsState.roleKey);
  };

  const handleCouponFound = (coupon: Coupon) => {
    setCoupons(prev => [...prev, coupon]);
  };

  const handleCouponRemoved = (couponId: number) => {
    setCoupons(prev => prev.filter(c => c.id !== couponId));
  };

  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    let cardInstance: any = null;
    let isMounted = true;

    const loadSquare = async () => {
      // Skip Square initialization if total is zero (covered by passes/discounts)
      const finalTotal = calculateFinalTotalRef.current();
      if (finalTotal <= 0) {
        console.log('Total is $0 or less, skipping Square initialization');
        setIsCardLoading(false);
        setIsInitialized(true); // Mark as initialized to allow form submission
        return;
      }

      // Prevent multiple initializations
      if (isInitializedRef.current) return;

      if (!(window as any).Square) {
        script = document.createElement('script');
        script.src = SQUARE_SCRIPT_URL;
        script.async = true;
        script.onload = initSquare;
        document.body.appendChild(script);
      } else {
        initSquare();
      }
    };

    const initSquare = async () => {
      try {
        if (!isMounted) return;

        setIsCardLoading(true);

        // Clear any existing card container content
        const cardContainer = document.getElementById('card-container');
        if (!cardContainer) {
          console.error('Card container not found');
          return;
        }
        cardContainer.innerHTML = '';

        const payments = (window as any).Square.payments(
          SQUARE_APP_ID,
          SQUARE_LOCATION_ID,
        );
        cardInstance = await payments.card();

        if (!isMounted) {
          cardInstance.destroy();
          return;
        }

        await cardInstance.attach('#card-container');
        setCard(cardInstance);
        setStatus('');
        setIsInitialized(true);
        console.log('Square card initialized successfully');
      } catch (err) {
        console.error('Square init failed:', err);
        if (isMounted) {
          setStatus('Failed to initialize Square Payments.');
          setHasError(true);
        }
      } finally {
        if (isMounted) {
          setIsCardLoading(false);
        }
      }
    };

    loadSquare();

    // Cleanup function
    return () => {
      isMounted = false;
      setHasError(false);
      setIsInitialized(false); // Reset so card can be reinitialized when coupons/passes change
      if (cardInstance) {
        try {
          cardInstance.destroy();
          setCard(null); // Clear the card state after destroying the instance
        } catch (error) {
          console.error('Error destroying card instance:', error);
        }
      }
      // Clear the card container HTML
      const cardContainer = document.getElementById('card-container');
      if (cardContainer) {
        cardContainer.innerHTML = '';
      }
      if (script && document.body.contains(script)) {
        try {
          document.body.removeChild(script);
        } catch (error) {
          console.error('Error removing script:', error);
        }
      }
    };
  }, [selectedAdultPasses, selectedChildPasses]);

  const reinitializeSquare = async () => {
    try {
      setHasError(false);
      setStatus('');
      setIsCardLoading(true);
      setIsInitialized(false);

      // Clear any existing card container content
      const cardContainer = document.getElementById('card-container');
      if (!cardContainer) {
        console.error('Card container not found');
        return;
      }
      cardContainer.innerHTML = '';

      // Destroy existing card instance if it exists
      if (card) {
        try {
          card.destroy();
        } catch (error) {
          console.error('Error destroying existing card:', error);
        }
      }

      // Reinitialize Square
      const payments = (window as any).Square.payments(
        SQUARE_APP_ID,
        SQUARE_LOCATION_ID,
      );
      const newCardInstance = await payments.card();
      await newCardInstance.attach('#card-container');
      setCard(newCardInstance);
      setStatus('');
      setIsInitialized(true);
    } catch (err) {
      console.error('Square reinitialization failed:', err);
      setStatus('Failed to reinitialize Square Payments. Please try again.');
      setHasError(true);
    } finally {
      setIsCardLoading(false);
    }
  };

  const calculateTotal = () => {
    switch (paymentType) {
      case 'skating':
        // For skating sessions (ticket booking)
        const skatingData = bookingData as BookingData;
        if (!skatingData.selectedTimeSlot || !skatingData.skatersInfo) {
          return 0;
        }

        const { adultPrice, childPrice, custom15PackDiscountPercentage } =
          skatingData.selectedTimeSlot;

        // Count adult and child tickets
        const adultCount = skatingData.skatersInfo.filter(
          (skater: SkaterInfo) => skater.isAdult,
        ).length;
        const childCount = skatingData.skatersInfo.filter(
          (skater: SkaterInfo) => skater.isChild,
        ).length;

        // Calculate total
        const adultTotal = adultCount * adultPrice;
        const childTotal = childCount * childPrice;
        const baseTotal = adultTotal + childTotal;

        // Apply 15-pack discount if applicable
        const totalTickets = adultCount + childCount;
        if (totalTickets >= 15 && custom15PackDiscountPercentage > 0) {
          const discountAmount =
            (baseTotal * custom15PackDiscountPercentage) / 100;
          return baseTotal - discountAmount;
        }

        return baseTotal;

      case 'hockey':
        // For hockey lessons
        const hockeyData = bookingData as HockeyLessonData;
        if (!hockeyData.selectedLesson || !hockeyData.participantsInfo) {
          return 0;
        }

        const participantCount = hockeyData.participantsInfo.length;
        return participantCount * hockeyData.selectedLesson.price;

      case 'lessons':
        // For skating lessons
        const lessonData = bookingData as SkatingLessonData;
        if (!lessonData.selectedLesson || !lessonData.participantsInfo) {
          return 0;
        }

        const lessonParticipantCount = lessonData.participantsInfo.length;
        return lessonParticipantCount * lessonData.selectedLesson.price;

      case 'parties':
        // For parties
        const partyData = bookingData as PartyData;
        const packagePrice = partyData.selectedPartyPackage?.price || 0;
        const extrasPrice = partyData.selectedExtras.reduce(
          (total, extraSelection) =>
            total + extraSelection.extra.price * extraSelection.quantity,
          0,
        );
        const pizzasPrice = partyData.selectedPizzas.reduce(
          (total, pizzaSelection) =>
            total + pizzaSelection.pizza.price * pizzaSelection.quantity,
          0,
        );
        return packagePrice + extrasPrice + pizzasPrice;

      case 'passes':
        // For skating passes
        const skatingPassData = bookingData as SkatingPassData;
        return skatingPassData.selectedPrice || 0;

      default:
        return 0;
    }
  };

  /**
   * Get day of week code from date string (M, T, W, TH, F, S, SU)
   */
  const getDayOfWeekCode = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayMap: Record<number, string> = {
      0: 'SU',
      1: 'M',
      2: 'T',
      3: 'W',
      4: 'TH',
      5: 'F',
      6: 'S',
    };
    return dayMap[day] || '';
  };

  /**
   * Convert time slot string (e.g., "10:00 AM" or "10:00") to numeric format (e.g., 1000)
   */
  const parseTimeSlotToNumber = (timeSlot: string): number | null => {
    if (!timeSlot) return null;

    // Handle formats like "10:00 AM", "10:00 PM", or "10:00"
    let timeStr = timeSlot.trim().toUpperCase();

    // Remove AM/PM and extract time
    const isPM = timeStr.includes('PM');
    const isAM = timeStr.includes('AM');
    timeStr = timeStr.replace(/\s*(AM|PM)\s*/i, '');

    const [hoursStr, minutesStr] = timeStr.split(':');
    if (!hoursStr || !minutesStr) return null;

    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (isNaN(hours) || isNaN(minutes)) return null;

    // Convert 12-hour to 24-hour format
    if (isPM && hours !== 12) {
      hours += 12;
    } else if (isAM && hours === 12) {
      hours = 0;
    }

    return hours * 100 + minutes;
  };

  const parseDateOnly = (
    dateString: string | null | undefined,
  ): Date | null => {
    if (!dateString) return null;
    const datePart = dateString.split('T')[0];
    if (!datePart) return null;

    const [yearStr, monthStr, dayStr] = datePart.split('-');
    if (!yearStr || !monthStr || !dayStr) return null;

    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);

    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }

    return new Date(Date.UTC(year, month - 1, day));
  };

  const isCouponValidForDate = (
    coupon: Coupon,
    bookingDate: string,
  ): boolean => {
    if (!bookingDate) {
      return true;
    }

    const bookingDateOnly = parseDateOnly(bookingDate);
    if (!bookingDateOnly) {
      return true;
    }

    const validFromDate = parseDateOnly(coupon.validFrom);
    if (
      validFromDate !== null &&
      bookingDateOnly.getTime() < validFromDate.getTime()
    ) {
      return false;
    }

    const validToDate = parseDateOnly(coupon.validTo);
    if (
      validToDate !== null &&
      bookingDateOnly.getTime() > validToDate.getTime()
    ) {
      return false;
    }

    return true;
  };

  /**
   * Check if coupon is valid for the booking date (applicableDays)
   */
  const isCouponValidForDay = (
    coupon: Coupon,
    bookingDate: string,
  ): boolean => {
    // If no applicableDays specified (null), coupon is valid for all days
    if (!coupon.applicableDays || coupon.applicableDays === null) {
      return true;
    }

    const dayOfWeek = getDayOfWeekCode(bookingDate);
    const applicableDays = coupon.applicableDays.split(',').map(d => d.trim());
    return applicableDays.includes(dayOfWeek);
  };

  /**
   * Check if coupon is valid for the booking time (timeRange)
   */
  const isCouponValidForTime = (coupon: Coupon, timeSlot: string): boolean => {
    // If no time range specified (both null), coupon is valid for all hours
    if (
      (!coupon.timeRangeFrom || coupon.timeRangeFrom === null) &&
      (!coupon.timeRangeTo || coupon.timeRangeTo === null)
    ) {
      return true;
    }

    const bookingTime = parseTimeSlotToNumber(timeSlot);
    if (bookingTime === null) return true; // If we can't parse time, allow it

    const rangeFrom = coupon.timeRangeFrom || 0;
    const rangeTo = coupon.timeRangeTo || 2359;

    return bookingTime >= rangeFrom && bookingTime <= rangeTo;
  };

  /**
   * Check if coupon is valid for the current booking
   */
  const isCouponValidForBooking = (coupon: Coupon): boolean => {
    if (paymentType !== 'skating') return true;

    const skatingData = bookingData as BookingData;
    if (!skatingData.selectedTimeSlot) return true;

    const { date, timeSlot } = skatingData.selectedTimeSlot;

    // Check day validity
    if (!isCouponValidForDay(coupon, date)) {
      return false;
    }

    // Check time validity
    if (!isCouponValidForTime(coupon, timeSlot)) {
      return false;
    }

    // Check date range validity
    if (!isCouponValidForDate(coupon, date)) {
      return false;
    }

    return true;
  };

  /**
   * Calculate discount value for a coupon when applied to a specific ticket type
   */
  const getCouponDiscountForTicket = (
    coupon: Coupon,
    ticketPrice: number,
  ): number => {
    if (coupon.discountType === 'Percentage') {
      return (ticketPrice * coupon.discountValue) / 100;
    } else if (coupon.discountType === 'Fixed') {
      return Math.min(coupon.discountValue, ticketPrice);
    }
    return 0;
  };

  /**
   * Calculate total coupon discount following priority rules:
   * - Each coupon applies to one ticket only (one-to-one)
   * - Always apply to Adult tickets first when available (higher price = maximum discount)
   * - If multiple coupons: apply sequentially to adult tickets until all are used
   * - If later coupon has higher discount than one already on adult, swap them (move higher to adult, lower to child)
   * - Only apply to child tickets when no adult tickets are available
   */
  const calculateCouponDiscount = () => {
    if (coupons.length === 0 || paymentType !== 'skating') {
      return 0;
    }

    const skatingData = bookingData as BookingData;
    if (!skatingData.selectedTimeSlot || !skatingData.skatersInfo) {
      return 0;
    }

    const { adultPrice, childPrice } = skatingData.selectedTimeSlot;
    const { adultCount, childCount } = getTicketCounts();

    // If no tickets, no discount
    if (adultCount === 0 && childCount === 0) {
      return 0;
    }

    // Filter coupons to only include those valid for the current booking
    const validCoupons = coupons.filter(coupon =>
      isCouponValidForBooking(coupon),
    );

    // Array to track which coupons are applied to which ticket types
    // Each entry: { coupon, appliedTo: 'adult' | 'child', discountValue: number }
    type CouponAssignment = {
      coupon: Coupon;
      appliedTo: 'adult' | 'child';
      discountValue: number;
    };
    const assignments: CouponAssignment[] = [];

    // Process valid coupons in the order they were added
    for (const coupon of validCoupons) {
      const adultDiscount = getCouponDiscountForTicket(coupon, adultPrice);
      const childDiscount = getCouponDiscountForTicket(coupon, childPrice);

      // Count how many adult and child tickets have coupons already
      const adultCouponsUsed = assignments.filter(
        a => a.appliedTo === 'adult',
      ).length;
      const childCouponsUsed = assignments.filter(
        a => a.appliedTo === 'child',
      ).length;

      // Check if we can apply to an adult ticket
      const canApplyToAdult = adultCouponsUsed < adultCount;
      const canApplyToChild = childCouponsUsed < childCount;

      if (canApplyToAdult) {
        // Check if there's an existing adult assignment with lower discount
        // This implements the swap rule: if later coupon has higher discount, swap them
        const existingAdultAssignment = assignments.find(
          a => a.appliedTo === 'adult' && a.discountValue < adultDiscount,
        );

        if (existingAdultAssignment) {
          // Swap: move higher discount to adult, lower to child
          const lowerCoupon = existingAdultAssignment.coupon;
          const lowerDiscountOnChild = getCouponDiscountForTicket(
            lowerCoupon,
            childPrice,
          );

          // Update the existing assignment to use the new (higher) coupon
          existingAdultAssignment.coupon = coupon;
          existingAdultAssignment.discountValue = adultDiscount;

          // If we can apply the lower discount to a child, do so
          if (canApplyToChild) {
            assignments.push({
              coupon: lowerCoupon,
              appliedTo: 'child',
              discountValue: lowerDiscountOnChild,
            });
          }
          // If we can't apply to child, the lower coupon is effectively "lost"
          // (it's been replaced by the higher discount on adult)
        } else {
          // Always prioritize adult tickets when available, regardless of discount value
          // This ensures maximum discount is applied (adult tickets are higher priced)
          // Only apply to child if no adult tickets are available
          assignments.push({
            coupon,
            appliedTo: 'adult',
            discountValue: adultDiscount,
          });
        }
      } else if (canApplyToChild) {
        // No adult tickets available, apply to child
        assignments.push({
          coupon,
          appliedTo: 'child',
          discountValue: childDiscount,
        });
      }
      // If neither adult nor child tickets available, coupon cannot be applied
    }

    // Sum up all the discounts
    return assignments.reduce((total, assignment) => {
      return total + assignment.discountValue;
    }, 0);
  };

  const calculate15PackDiscount = () => {
    if (paymentType !== 'skating') {
      return 0;
    }

    const skatingData = bookingData as BookingData;
    if (!skatingData.selectedTimeSlot || !skatingData.skatersInfo) {
      return 0;
    }

    const { adultPrice, childPrice, custom15PackDiscountPercentage } =
      skatingData.selectedTimeSlot;

    // Count adult and child tickets
    const adultCount = skatingData.skatersInfo.filter(
      (skater: SkaterInfo) => skater.isAdult,
    ).length;
    const childCount = skatingData.skatersInfo.filter(
      (skater: SkaterInfo) => skater.isChild,
    ).length;

    const totalTickets = adultCount + childCount;
    if (totalTickets >= 15 && custom15PackDiscountPercentage > 0) {
      const adultTotal = adultCount * adultPrice;
      const childTotal = childCount * childPrice;
      const baseTotal = adultTotal + childTotal;
      return (baseTotal * custom15PackDiscountPercentage) / 100;
    }

    return 0;
  };

  const getTicketCounts = () => {
    if (paymentType !== 'skating') {
      return { adultCount: 0, childCount: 0 };
    }

    const skatingData = bookingData as BookingData;
    if (!skatingData.skatersInfo) {
      return { adultCount: 0, childCount: 0 };
    }

    const adultCount = skatingData.skatersInfo.filter(
      (skater: SkaterInfo) => skater.isAdult,
    ).length;
    const childCount = skatingData.skatersInfo.filter(
      (skater: SkaterInfo) => skater.isChild,
    ).length;

    return { adultCount, childCount };
  };

  const getAdultSkaterEmails = () => {
    if (paymentType !== 'skating') {
      return [];
    }

    const skatingData = bookingData as BookingData;
    if (!skatingData.skatersInfo) {
      return [];
    }

    // Extract emails from adult skaters only
    const adultEmails = skatingData.skatersInfo
      .filter((skater: SkaterInfo) => skater.isAdult)
      .map((skater: SkaterInfo) => skater.email)
      .filter((email: string) => email && email.trim() !== '');

    console.log('Adult skater emails:', adultEmails);
    return adultEmails;
  };

  const getChildSkaterEmails = () => {
    if (paymentType !== 'skating') {
      return [];
    }

    const skatingData = bookingData as BookingData;
    if (!skatingData.skatersInfo) {
      return [];
    }

    // Extract emails from child skaters only
    const childEmails = skatingData.skatersInfo
      .filter((skater: SkaterInfo) => skater.isChild)
      .map((skater: SkaterInfo) => skater.email)
      .filter((email: string) => email && email.trim() !== '');

    console.log('Child skater emails:', childEmails);
    return childEmails;
  };

  const calculatePassDiscount = () => {
    if (paymentType !== 'skating') {
      return 0;
    }

    const skatingData = bookingData as BookingData;
    if (!skatingData.skatersInfo || !skatingData.selectedTimeSlot) {
      return 0;
    }

    const { adultCount, childCount } = getTicketCounts();
    const adultSkaterEmails = getAdultSkaterEmails();
    const childSkaterEmails = getChildSkaterEmails();

    let passDiscount = 0;

    // Apply adult pass discounts - iterate through all selected passes (one pass per ticket)
    if (selectedAdultPasses.length > 0) {
      console.log('=== ADULT PASS DISCOUNT CALCULATION ===');
      console.log(
        'Number of selected adult passes:',
        selectedAdultPasses.length,
      );
      console.log('Adult count:', adultCount);
      console.log('Adult skater emails for validation:', adultSkaterEmails);

      // Apply discount for each selected pass, up to the number of adult tickets
      const passesToApply = Math.min(selectedAdultPasses.length, adultCount);

      for (let i = 0; i < passesToApply; i++) {
        const pass = selectedAdultPasses[i];
        console.log(`Processing adult pass ${i + 1}:`, pass.passName);
        console.log('Pass is active:', pass.isActive);
        console.log('Pass redeemed count:', pass.redeemedCount);
        console.log('Pass max uses:', pass.maxUses);

        const emailValidationResult = canPassBeUsedBySkaters(
          pass,
          adultSkaterEmails,
        );
        console.log('Email validation result:', emailValidationResult);

        if (
          pass.isActive &&
          HelperMethods.canRedeemSkatingPass(pass) &&
          emailValidationResult
        ) {
          console.log(`✅ Applying adult pass discount for pass ${i + 1}`);
          passDiscount += skatingData.selectedTimeSlot.adultPrice;
        } else {
          console.log(`❌ Not applying adult pass discount for pass ${i + 1}`);
        }
      }
      console.log('Total adult pass discount:', passDiscount);
      console.log('=== END ADULT PASS DISCOUNT CALCULATION ===');
    }

    // Apply child pass discounts - iterate through all selected passes (one pass per ticket)
    if (selectedChildPasses.length > 0) {
      console.log('=== CHILD PASS DISCOUNT CALCULATION ===');
      console.log(
        'Number of selected child passes:',
        selectedChildPasses.length,
      );
      console.log('Child count:', childCount);
      console.log('Child skater emails for validation:', childSkaterEmails);

      // Apply discount for each selected pass, up to the number of child tickets
      const passesToApply = Math.min(selectedChildPasses.length, childCount);

      for (let i = 0; i < passesToApply; i++) {
        const pass = selectedChildPasses[i];
        console.log(`Processing child pass ${i + 1}:`, pass.passName);
        console.log('Pass is active:', pass.isActive);
        console.log('Pass redeemed count:', pass.redeemedCount);
        console.log('Pass max uses:', pass.maxUses);

        const emailValidationResult = canPassBeUsedBySkaters(
          pass,
          childSkaterEmails,
        );
        console.log('Email validation result:', emailValidationResult);

        if (
          pass.isActive &&
          HelperMethods.canRedeemSkatingPass(pass) &&
          emailValidationResult
        ) {
          console.log(`✅ Applying child pass discount for pass ${i + 1}`);
          passDiscount += skatingData.selectedTimeSlot.childPrice;
        } else {
          console.log(`❌ Not applying child pass discount for pass ${i + 1}`);
        }
      }
      console.log('Total pass discount (adult + child):', passDiscount);
      console.log('=== END CHILD PASS DISCOUNT CALCULATION ===');
    }

    return passDiscount;
  };

  // Helper function to check if a pass can be used by any of the skaters
  const canPassBeUsedBySkaters = (
    pass: BookedSkatingPass,
    skaterEmails: string[],
  ): boolean => {
    // Passes require a matching skater email (pass holder only)
    if (!skaterEmails || skaterEmails.length === 0) {
      return false;
    }

    // If pass has no customer email, it can't be matched
    if (!pass.customerEmail) {
      return false;
    }

    // Check if any skater email matches the pass owner email
    return skaterEmails.some(
      email => email.toLowerCase() === pass.customerEmail?.toLowerCase(),
    );
  };

  const calculateDiscount = () => {
    if (paymentType === 'skating') {
      const skatingData = bookingData as BookingData;
      if (!skatingData.skatersInfo) {
        return 0;
      }

      const adultCount = skatingData.skatersInfo.filter(
        (skater: SkaterInfo) => skater.isAdult,
      ).length;
      const childCount = skatingData.skatersInfo.filter(
        (skater: SkaterInfo) => skater.isChild,
      ).length;

      // Apply $10 discount for each complete set of 2 adults and 2 children
      let familyDiscount = 0;

      // Calculate how many complete sets of 2 adults we can make
      const adultSets = Math.floor(adultCount / 2);

      // Calculate how many complete sets of 2 children we can make
      const childSets = Math.floor(childCount / 2);

      // The number of complete family sets (2 adults + 2 children) is the minimum
      const familySets = Math.min(adultSets, childSets);

      // Apply $10 discount for each complete family set
      familyDiscount = familySets * 10;

      return familyDiscount;
    }

    return 0;
  };

  const calculateSubtotal = () => {
    return (
      calculateTotal() -
      calculateDiscount() -
      calculateCouponDiscount() -
      calculate15PackDiscount() -
      calculatePassDiscount()
    );
  };

  const calculateConvenienceFee = () => {
    const subtotal = calculateSubtotal();
    return subtotal * 0.03; // 3% convenience fee
  };

  const calculateFinalTotal = () => {
    const total = calculateSubtotal() + calculateConvenienceFee();
    return Math.max(0, total);
  };
  calculateFinalTotalRef.current = calculateFinalTotal;

  const handleSubmitPayment = async () => {
    setIsProcessing(true);

    const finalTotal = calculateFinalTotal();
    console.log('Final total:', finalTotal);

    // If total is zero (passes cover entire cost), skip tokenization
    if (finalTotal <= 0) {
      console.log('Total is zero, skipping tokenization');
      setStatus('Processing with passes...');

      const data: SquarePaymentInfo = {
        paymentType,
        nonce: null, // No payment required
        amount: 0,
        currency: 'USD',
        description: `Skating Session: ${HelperMethods.formatDate((bookingData as BookingData).selectedTimeSlot?.date || '')} ${HelperMethods.formatTime12Hour((bookingData as BookingData).selectedTimeSlot?.timeSlot || '')} ${(bookingData as BookingData).skatersInfo?.length || 0} skaters ${(bookingData as BookingData).skatersInfo?.map((skater: SkaterInfo) => skater.firstName).join(', ') || ''} | BarcodeId: ${(bookingData as BookingData).barcodeId || 'N/A'} | Paid with Passes`,
      };

      console.log('Payment data being sent (passes only):', data);
      setIsProcessing(false);
      console.log('Calling onStepComplete with paymentInfo');
      // Create array with all selected passes that pass validation
      const { adultCount, childCount } = getTicketCounts();
      const selectedSkatingPasses: BookedSkatingPass[] = [];

      // Add all valid adult passes (one pass per adult ticket)
      const adultSkaterEmails = getAdultSkaterEmails();
      if (selectedAdultPasses.length > 0) {
        const passesToApply = Math.min(selectedAdultPasses.length, adultCount);
        for (let i = 0; i < passesToApply; i++) {
          const pass = selectedAdultPasses[i];
          if (canPassBeUsedBySkaters(pass, adultSkaterEmails)) {
            selectedSkatingPasses.push(pass);
          }
        }
      }

      // Add all valid child passes (one pass per child ticket)
      const childSkaterEmails = getChildSkaterEmails();
      if (selectedChildPasses.length > 0) {
        const passesToApply = Math.min(selectedChildPasses.length, childCount);
        for (let i = 0; i < passesToApply; i++) {
          const pass = selectedChildPasses[i];
          if (canPassBeUsedBySkaters(pass, childSkaterEmails)) {
            selectedSkatingPasses.push(pass);
          }
        }
      }

      onStepComplete({
        paymentInfo: data,
        couponAttached: coupons.length > 0 ? coupons : null,
        selectedSkatingPasses,
      });
      return;
    }

    if (!card) {
      console.error('Card instance is null, cannot tokenize');
      console.log('Card state:', card);
      console.log('Is initialized:', isInitialized);
      console.log('Is card loading:', isCardLoading);
      setIsProcessing(false);
      return;
    }

    try {
      setStatus('Processing...');
      console.log('Attempting to tokenize card...');
      const result = await card.tokenize();
      console.log('Card tokenization result:', result);

      console.log(bookingData);

      if (result.status === 'OK') {
        let squareDescription = `Skating Session: ${HelperMethods.formatDate((bookingData as BookingData).selectedTimeSlot?.date || '')} ${HelperMethods.formatTime12Hour((bookingData as BookingData).selectedTimeSlot?.timeSlot || '')} ${(bookingData as BookingData).skatersInfo?.length || 0} skaters ${(bookingData as BookingData).skatersInfo?.map((skater: SkaterInfo) => skater.firstName).join(', ') || ''} | BarcodeId: ${(bookingData as BookingData).barcodeId || 'N/A'}`;

        console.log(paymentType);
        if (paymentType === 'lessons') {
          const skatingData = bookingData as SkatingLessonData;

          console.log(skatingData);
          const lessonTitle =
            skatingData.selectedLesson?.title ||
            skatingData.selectedLesson?.lessonKey ||
            'Skating Lesson';

          console.log(lessonTitle);
          const participantCount = skatingData.participantsInfo?.length || 0;
          const participantNames =
            skatingData.participantsInfo
              ?.map((participant: ParticipantInfo) => participant.firstName)
              .filter(Boolean)
              .join(', ') || '';

          squareDescription = `Skating Lesson: ${lessonTitle} ${participantCount} participants${participantNames ? ` ${participantNames}` : ''} | BarcodeId: ${skatingData.barcodeId || 'N/A'}`;
        }

        if (paymentType === 'hockey') {
          const hockeyData = bookingData as HockeyLessonData;
          const lessonTitle =
            hockeyData.selectedLesson?.title || 'Hockey Lesson';
          const participantCount = hockeyData.participantsInfo?.length || 0;
          const participantNames =
            hockeyData.participantsInfo
              ?.map((participant: ParticipantInfo) => participant.firstName)
              .filter(Boolean)
              .join(', ') || '';
          squareDescription = `Hockey Lesson: ${lessonTitle} ${participantCount} participants${participantNames ? ` ${participantNames}` : ''} | BarcodeId: ${hockeyData.barcodeId || 'N/A'}`;
        }

        if (paymentType === 'passes') {
          const passData = bookingData as SkatingPassData;
          const passName = passData.selectedPass?.name || 'Skating Pass';
          const passType = passData.selectedPassType || 'Pass';
          const skaterName =
            passData.skaterInfo?.firstName && passData.skaterInfo?.lastName
              ? `${passData.skaterInfo.firstName} ${passData.skaterInfo.lastName}`
              : '';
          squareDescription = `Skating Pass: ${passName} (${passType})${skaterName ? ` for ${skaterName}` : ''} | BarcodeId: ${passData.barcodeId || 'N/A'}`;
        }

        if (paymentType === 'parties') {
          const partyData = bookingData as PartyData;
          const packageName =
            partyData.selectedPartyPackage?.name || 'Party Package';
          const partyDate = partyData.selectedTimeSlot?.date || '';
          const partyTime = partyData.selectedTimeSlot?.timeSlot || '';
          const customerName =
            partyData.customerInfo?.firstName &&
            partyData.customerInfo?.lastName
              ? `${partyData.customerInfo.firstName} ${partyData.customerInfo.lastName}`
              : '';
          squareDescription = `Party Package: ${packageName}${partyDate ? ` on ${partyDate}` : ''}${partyTime ? ` at ${partyTime}` : ''}${customerName ? ` for ${customerName}` : ''} | BarcodeId: ${partyData.barcodeId || 'N/A'}`;
        }

        const data: SquarePaymentInfo = {
          paymentType,
          nonce: result.token,
          amount: Math.round(calculateFinalTotal() * 100), // Convert dollars to cents
          currency: 'USD',
          description: squareDescription,
        };
        console.log('Payment data being sent:', data);
        setIsProcessing(false);
        console.log('Calling onStepComplete with paymentInfo');

        // Create array with all selected passes that pass validation
        const { adultCount, childCount } = getTicketCounts();
        const selectedSkatingPasses: BookedSkatingPass[] = [];

        // Add all valid adult passes (one pass per adult ticket)
        const adultSkaterEmails = getAdultSkaterEmails();
        if (selectedAdultPasses.length > 0) {
          const passesToApply = Math.min(
            selectedAdultPasses.length,
            adultCount,
          );
          for (let i = 0; i < passesToApply; i++) {
            const pass = selectedAdultPasses[i];
            if (canPassBeUsedBySkaters(pass, adultSkaterEmails)) {
              selectedSkatingPasses.push(pass);
            }
          }
        }

        // Add all valid child passes (one pass per child ticket)
        const childSkaterEmails = getChildSkaterEmails();
        if (selectedChildPasses.length > 0) {
          const passesToApply = Math.min(
            selectedChildPasses.length,
            childCount,
          );
          for (let i = 0; i < passesToApply; i++) {
            const pass = selectedChildPasses[i];
            if (canPassBeUsedBySkaters(pass, childSkaterEmails)) {
              selectedSkatingPasses.push(pass);
            }
          }
        }

        onStepComplete({
          paymentInfo: data,
          couponAttached: coupons.length > 0 ? coupons : null,
          selectedSkatingPasses,
        });
      } else {
        console.error('Card tokenization failed with result:', result);
        setStatus(
          `Card tokenization failed: ${result.errors?.map((e: any) => e.detail).join(', ') || 'Unknown error'}`,
        );
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error during payment processing:', error);
      setStatus('Payment processing failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleSkipPayment = async () => {
    setIsProcessing(true);
    setStatus('Processing...');

    try {
      console.log('Skipping payment (admin override)');

      // Create array with all selected passes that pass validation
      const { adultCount, childCount } = getTicketCounts();
      const selectedSkatingPasses: BookedSkatingPass[] = [];

      // Add all valid adult passes (one pass per adult ticket)
      const adultSkaterEmails = getAdultSkaterEmails();
      if (selectedAdultPasses.length > 0) {
        const passesToApply = Math.min(selectedAdultPasses.length, adultCount);
        for (let i = 0; i < passesToApply; i++) {
          const pass = selectedAdultPasses[i];
          if (canPassBeUsedBySkaters(pass, adultSkaterEmails)) {
            selectedSkatingPasses.push(pass);
          }
        }
      }

      // Add all valid child passes (one pass per child ticket)
      const childSkaterEmails = getChildSkaterEmails();
      if (selectedChildPasses.length > 0) {
        const passesToApply = Math.min(selectedChildPasses.length, childCount);
        for (let i = 0; i < passesToApply; i++) {
          const pass = selectedChildPasses[i];
          if (canPassBeUsedBySkaters(pass, childSkaterEmails)) {
            selectedSkatingPasses.push(pass);
          }
        }
      }

      console.log('Calling onStepComplete with null paymentInfo (admin skip)');
      setIsProcessing(false);

      onStepComplete({
        paymentInfo: null,
        couponAttached: coupons.length > 0 ? coupons : null,
        selectedSkatingPasses,
      });
    } catch (error) {
      console.error('Error during skip payment processing:', error);
      setStatus('Skip payment processing failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const isFormValid = () => {
    const finalTotal = calculateFinalTotal();

    // If total is zero (passes cover entire cost), no card validation needed
    if (finalTotal <= 0) {
      return true;
    }

    // With Square, we just need to check if the card is initialized
    const isValid = card !== null;
    console.log('Form validation:', {
      isValid,
      card: !!card,
      isProcessing,
      isCardLoading,
      finalTotal,
    });
    return isValid;
  };

  const finalTotalForRender = calculateFinalTotal();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Payment Information
        </h2>
        <p className="text-gray-600">
          Complete your booking with secure payment
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Order Summary */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <MdReceipt className="text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">Order Summary</h3>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            {paymentType === 'skating' && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Session Date:</span>
                  <span className="font-medium">
                    {(bookingData as BookingData).selectedTimeSlot?.date
                      ? new Date(
                          (bookingData as BookingData).selectedTimeSlot!.date,
                        ).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'Not selected'}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Session Time:</span>
                  <span className="font-medium">
                    {(bookingData as BookingData).selectedTimeSlot?.timeSlot
                      ? HelperMethods.formatTime12Hour(
                          (bookingData as BookingData).selectedTimeSlot!
                            .timeSlot,
                        )
                      : 'Not selected'}
                  </span>
                </div>

                <div className="space-y-1">
                  <div className="text-gray-600 text-sm font-medium">
                    Ticket Breakdown:
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Adult Tickets (
                      {(bookingData as BookingData).skatersInfo?.filter(
                        (skater: SkaterInfo) => skater.isAdult,
                      ).length || 0}
                      ):
                    </span>
                    <span className="font-medium">
                      $
                      {(
                        ((bookingData as BookingData).skatersInfo?.filter(
                          (skater: SkaterInfo) => skater.isAdult,
                        ).length || 0) *
                        ((bookingData as BookingData).selectedTimeSlot
                          ?.adultPrice || 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Child Tickets (
                      {(bookingData as BookingData).skatersInfo?.filter(
                        (skater: SkaterInfo) => skater.isChild,
                      ).length || 0}
                      ):
                    </span>
                    <span className="font-medium">
                      $
                      {(
                        ((bookingData as BookingData).skatersInfo?.filter(
                          (skater: SkaterInfo) => skater.isChild,
                        ).length || 0) *
                        ((bookingData as BookingData).selectedTimeSlot
                          ?.childPrice || 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </>
            )}

            {(paymentType === 'hockey' || paymentType === 'lessons') && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Lesson Type:</span>
                  <span className="font-medium">
                    {paymentType === 'hockey' ? 'Hockey' : 'Skating'} Lesson
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-medium">
                    {(bookingData as HockeyLessonData | SkatingLessonData)
                      .selectedLesson?.title || 'Not selected'}
                  </span>
                </div>

                {/* <div className="flex justify-between">
                  <span className="text-gray-600">Lesson Description:</span>
                  <span className="font-medium text-sm">
                    {(bookingData as HockeyLessonData | SkatingLessonData)
                      .selectedLesson?.description || 'No description'}
                  </span>
                </div> */}

                <div className="space-y-1">
                  <div className="text-gray-600 text-sm font-medium">
                    Participant Breakdown:
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Participants (
                      {(bookingData as HockeyLessonData | SkatingLessonData)
                        .participantsInfo?.length || 0}
                      ):
                    </span>
                    <span className="font-medium">
                      $
                      {(
                        ((bookingData as HockeyLessonData | SkatingLessonData)
                          .participantsInfo?.length || 0) *
                        ((bookingData as HockeyLessonData | SkatingLessonData)
                          .selectedLesson?.price || 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </>
            )}

            {paymentType === 'parties' && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Party Package:</span>
                  <span className="font-medium">
                    {(bookingData as PartyData).selectedPartyPackage?.name ||
                      'Not selected'}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Package Price:</span>
                  <span className="font-medium">
                    $
                    {(bookingData as PartyData).selectedPartyPackage?.price ||
                      0}
                  </span>
                </div>

                {(bookingData as PartyData).selectedPizzas.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-gray-600 text-sm font-medium">
                      Selected Pizzas:
                    </div>
                    {(bookingData as PartyData).selectedPizzas.map(
                      pizzaSelection => (
                        <div
                          key={pizzaSelection.pizza.id}
                          className="flex justify-between text-sm"
                        >
                          <span className="text-gray-600">
                            {pizzaSelection.pizza.name} (x
                            {pizzaSelection.quantity})
                          </span>
                          <span className="font-medium">
                            $
                            {(
                              pizzaSelection.pizza.price *
                              pizzaSelection.quantity
                            ).toFixed(2)}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                )}

                {(bookingData as PartyData).selectedExtras.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-gray-600 text-sm font-medium">
                      Selected Extras:
                    </div>
                    {(bookingData as PartyData).selectedExtras.map(
                      extraSelection => (
                        <div
                          key={extraSelection.extra.id}
                          className="flex justify-between text-sm"
                        >
                          <span className="text-gray-600">
                            {extraSelection.extra.name} (x
                            {extraSelection.quantity})
                          </span>
                          <span className="font-medium">
                            $
                            {(
                              extraSelection.extra.price *
                              extraSelection.quantity
                            ).toFixed(2)}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </>
            )}

            {paymentType === 'passes' && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pass Type:</span>
                  <span className="font-medium">
                    {(bookingData as SkatingPassData).selectedPass?.name ||
                      'Not selected'}
                  </span>
                </div>

                {/* <div className="flex justify-between">
                  <span className="text-gray-600">Pass Description:</span>
                  <span className="font-medium text-sm">
                    {(bookingData as SkatingPassData).selectedPass
                      ?.description || 'No description'}
                  </span>
                </div> */}

                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-medium">
                    {(bookingData as SkatingPassData).selectedPassType ===
                    'adult'
                      ? 'Adult Pass'
                      : (bookingData as SkatingPassData).selectedPassType ===
                          'child'
                        ? 'Child Pass'
                        : 'Not selected'}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Max Uses:</span>
                  <span className="font-medium">
                    {(bookingData as SkatingPassData).selectedPass?.type
                      .toLowerCase()
                      .replace(' ', '_') === 'season_pass'
                      ? 'Unlimited'
                      : `${(bookingData as SkatingPassData).selectedPass?.maxUses || 0} sessions`}
                  </span>
                </div>

                <div className="space-y-1">
                  <div className="text-gray-600 text-sm font-medium">
                    Pricing Breakdown:
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {(bookingData as SkatingPassData).selectedPassType ===
                      'adult'
                        ? 'Adult Pass Price'
                        : (bookingData as SkatingPassData).selectedPassType ===
                            'child'
                          ? 'Child Pass Price'
                          : 'Pass Price'}
                      :
                    </span>
                    <span className="font-medium">
                      ${(bookingData as SkatingPassData).selectedPrice || 0}
                    </span>
                  </div>
                </div>
              </>
            )}

            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Before Discount:</span>
                <span>${calculateTotal().toFixed(2)}</span>
              </div>
              {calculateDiscount() > 0 && (
                <div className="flex justify-between text-green-600">
                  <span className="font-medium">
                    Family Discount ({Math.floor(calculateDiscount() / 10)} × 2
                    Adults + 2 Children):
                  </span>
                  <span className="font-medium">
                    -${calculateDiscount().toFixed(2)}
                  </span>
                </div>
              )}

              {calculate15PackDiscount() > 0 && (
                <div className="flex justify-between text-green-600">
                  <span className="font-medium">
                    Group Discount (
                    {(bookingData as BookingData).selectedTimeSlot
                      ?.custom15PackDiscountPercentage || 0}
                    %):
                  </span>
                  <span className="font-medium">
                    -${calculate15PackDiscount().toFixed(2)}
                  </span>
                </div>
              )}

              {calculateCouponDiscount() > 0 && (
                <div className="flex justify-between text-green-600">
                  <span className="font-medium">
                    Discount ({coupons.length} Coupon
                    {coupons.length !== 1 ? 's' : ''}):
                  </span>
                  <span className="font-medium">
                    -${calculateCouponDiscount().toFixed(2)}
                  </span>
                </div>
              )}

              {calculatePassDiscount() > 0 && (
                <div className="flex justify-between text-green-600">
                  <span className="font-medium">Pass Discount:</span>
                  <span className="font-medium">
                    -${calculatePassDiscount().toFixed(2)}
                  </span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span>${calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Convenience Fee (3%):</span>
                <span>${calculateConvenienceFee().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total Amount to Pay:</span>
                <span>${finalTotalForRender.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {paymentType === 'skating' && (
            <GetCouponDetails
              onCouponFound={handleCouponFound}
              onCouponRemoved={handleCouponRemoved}
              appliedCoupons={coupons}
              bookingData={bookingData as BookingData}
              paymentType={paymentType}
            />
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Your payment information is secure and encrypted. We use
              industry-standard SSL encryption to protect your data.
            </p>
          </div>
        </div>

        {/* Payment Form */}
        <div className="space-y-6">
          {/* Zero Total Message - Show when passes/coupons cover entire cost */}
          {finalTotalForRender <= 0 && paymentType === 'skating' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="text-md font-medium text-green-900">
                    {calculatePassDiscount() > 0 &&
                    calculateCouponDiscount() > 0
                      ? 'Payment is fully covered by passes and coupons'
                      : calculatePassDiscount() > 0
                        ? 'Payment is fully covered by passes'
                        : calculateCouponDiscount() > 0
                          ? 'Payment is fully covered by coupons'
                          : 'No payment required'}
                  </h3>
                  <p className="text-green-700 mt-1">
                    {calculatePassDiscount() > 0 &&
                    calculateCouponDiscount() > 0
                      ? 'Your selected passes and coupons cover the entire cost of this session. No payment required!'
                      : calculatePassDiscount() > 0
                        ? 'Your selected passes cover the entire cost of this session. No payment required!'
                        : calculateCouponDiscount() > 0
                          ? 'Your applied coupons cover the entire cost of this session. No payment required!'
                          : 'The entire cost is covered. No payment required!'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Only show payment details section when payment is required */}
          {finalTotalForRender > 0 && (
            <>
              <div className="flex items-center space-x-2">
                <MdCreditCard className="text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Payment Details
                </h3>
              </div>
              {isCardLoading && (
                <div className="flex items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-gray-600">Loading payment form...</p>
                  </div>
                </div>
              )}
              <div id="card-container" className="mb-4" />
              {status && (
                <div
                  className={`mt-4 p-4 rounded-lg border-2 flex items-start space-x-3 ${
                    status.toLowerCase().includes('fail') ||
                    status.toLowerCase().includes('error')
                      ? 'bg-red-50 border-red-500'
                      : status === 'Processing...'
                        ? 'bg-blue-50 border-blue-500'
                        : 'bg-green-50 border-green-500'
                  }`}
                >
                  {(status.toLowerCase().includes('fail') ||
                    status.toLowerCase().includes('error')) && (
                    <MdError className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  {status === 'Processing...' && (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 flex-shrink-0 mt-0.5"></div>
                  )}
                  {!status.toLowerCase().includes('fail') &&
                    !status.toLowerCase().includes('error') &&
                    status !== 'Processing...' && (
                      <MdCheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    )}
                  <div className="flex-1">
                    <p
                      className={`font-semibold text-base ${
                        status.toLowerCase().includes('fail') ||
                        status.toLowerCase().includes('error')
                          ? 'text-red-800'
                          : status === 'Processing...'
                            ? 'text-blue-800'
                            : 'text-green-800'
                      }`}
                    >
                      {status}
                    </p>
                  </div>
                </div>
              )}
              {hasError && (
                <div className="mt-4">
                  <button
                    onClick={reinitializeSquare}
                    disabled={isCardLoading}
                    className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                  >
                    {isCardLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Reinitializing...</span>
                      </>
                    ) : (
                      <>
                        <MdCreditCard className="w-4 h-4" />
                        <span>Retry Payment Form</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Passes Section - Always show for skating */}
          {paymentType === 'skating' && isAuthenticated && (
            <AvailablePasses
              selectedAdultPasses={selectedAdultPasses}
              selectedChildPasses={selectedChildPasses}
              onSelectAdultPasses={setSelectedAdultPasses}
              onSelectChildPasses={setSelectedChildPasses}
              maxAdultPasses={getTicketCounts().adultCount}
              maxChildPasses={getTicketCounts().childCount}
              adultSkaterEmails={getAdultSkaterEmails()}
              childSkaterEmails={getChildSkaterEmails()}
              sessionDate={
                paymentType === 'skating'
                  ? (bookingData as BookingData).selectedTimeSlot?.date
                  : undefined
              }
            />
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        {isAdmin() &&
          (paymentType === 'skating' || paymentType === 'parties') && (
            <button
              onClick={handleSkipPayment}
              disabled={isProcessing}
              className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base order-1 sm:order-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <MdCreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Skip Payment</span>
                </>
              )}
            </button>
          )}

        <button
          onClick={onPrevious}
          className="w-full sm:w-auto px-4 sm:px-6 py-3 text-gray-700 bg-gray-200 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm sm:text-base order-2 sm:order-1"
        >
          Back
        </button>
        <button
          onClick={handleSubmitPayment}
          disabled={!isFormValid() || isProcessing}
          className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base order-1 sm:order-2"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Processing...</span>
            </>
          ) : finalTotalForRender <= 0 ? (
            <>
              <MdCheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Complete Booking</span>
            </>
          ) : (
            <>
              <MdCreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Complete Payment</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
